/**
 * Colored-balance convention (STEP-08) — ONE rule everywhere, always from the
 * OWNER's point of view:
 *
 * - GREEN  {@link OWED_TO_OWNER}: money owed TO the owner on «+» ledger rows
 *   (ISSUE / PURCHASE that grow the counterparty's debt) and non-negative
 *   profit. Green = the owner's asset.
 * - RED    {@link OWNER_OWES}: the owner's own debts and outgoing money —
 *   sklad debts, «−» ledger rows, expenses, negative profit — AND a store's
 *   outstanding balance (money still to collect from the store; owner preference).
 * - ORANGE {@link UNUSUAL_NEGATIVE}: a balance with an unexpected sign (the
 *   store overpaid, the sklad owes the owner) — needs attention, neither a
 *   normal asset nor a normal debt.
 * - BLUE   {@link CASH_RECEIVED}: cash actually received from a store
 *   (STORE_PAYMENT in the journal, where rows have no ledger direction) —
 *   money in hand rather than a claim or a debt.
 */
export const OWED_TO_OWNER = '#3f8600';
export const OWNER_OWES = '#cf1322';
export const UNUSUAL_NEGATIVE = '#d46b08';
export const CASH_RECEIVED = '#0958d9';

/** Store balance = an OUTSTANDING amount the store still owes the owner (money
 * to collect): shown RED, orange if it goes unexpectedly negative (overpaid).
 * (Per owner preference, a store's remaining balance reads as a debt to chase
 * rather than a booked asset — so it is red, matching the sklad-debt colour and
 * the storefront.) The «+»/«−» ledger ROW colours are unaffected. */
export function storeBalanceColor(kopeks: number): string {
  return kopeks >= 0 ? OWNER_OWES : UNUSUAL_NEGATIVE;
}

/** Sklad debt = what the owner owes the sklad: red, orange if negative. */
export function skladDebtColor(kopeks: number): string {
  return kopeks >= 0 ? OWNER_OWES : UNUSUAL_NEGATIVE;
}

/** Ledger balances share the entity's convention. */
export function balanceColor(kind: 'store' | 'sklad', kopeks: number): string {
  return kind === 'store' ? storeBalanceColor(kopeks) : skladDebtColor(kopeks);
}

/**
 * Soft-tint background from a «#rrggbb» accent: appends an alpha byte so the
 * result is a translucent «#rrggbbaa» that reads on both light and dark
 * themes. Returns undefined for missing/malformed input (no tint applied).
 * Used to shade a sklad's column in the store day-grid.
 */
export function hexTint(hex: string | null | undefined, alpha: number): string | undefined {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return undefined;
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${a}`;
}
